import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Type } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface HeadlineABOrderCardProps {
  variation: HeadlineOverlayVariation;
  orderIndex: number;
  isPrimary: boolean;
}

export function HeadlineABOrderCard({ variation, orderIndex, isPrimary }: HeadlineABOrderCardProps) {
  useLanguage();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: variation.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 p-3 rounded-xl border border-border bg-card transition-all",
        isDragging && "opacity-50 shadow-lg scale-[1.02]",
        "hover:border-primary/30 hover:shadow-sm"
      )}
    >

      {/* Mini Preview */}
      <div className="w-10 h-10 rounded-lg bg-muted/50 flex items-center justify-center border border-border/50 overflow-hidden shrink-0">
        <Type className="h-4 w-4 text-muted-foreground" />
      </div>

      {/* Name with Tooltip */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="flex-1 text-sm font-medium text-foreground truncate min-w-0">
              {variation.name || translateInterfaceText("Sem nome")}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p className="text-xs">{variation.name || translateInterfaceText("Sem nome")}</p>
            {variation.headline?.text && (
              <p className="text-xs text-muted-foreground mt-1 max-w-[200px] truncate">
                {variation.headline.text}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Status Badge */}
      <Badge 
        variant="outline" 
        className={cn(
          "shrink-0 text-[10px] px-2 py-0.5",
          isPrimary 
            ? "bg-primary/10 text-primary border-primary/20" 
            : "bg-muted text-muted-foreground border-border"
        )}
      >
        {isPrimary ? translateInterfaceText("Principal") : translateInterfaceText("Em teste")}
      </Badge>
    </div>
  );
}
